import PubSub from "pubsub-js";
import { type FC, useEffect, useMemo, useState } from "react";
import { formatDateTimeFromISO, Modal, TabDialogComponent, TabItem, TriggerButton } from "widgets";
import { useOverlay } from "widgets/hooks";

import { PubSubActions } from "@/global/pubsub-actions.ts";
import type { ProjectFile } from "@/graphql.ts";
import { OpenInNewTabButton } from "@/modules/core/components/OpenInNewTabButton";
import { FileCard } from "@/modules/files/components/FileCard";
import { FileDownloadButton } from "@/modules/files/components/FileDownloadButton";
import { FilePreviewControlledDialog } from "@/modules/files/components/FilePreviewControlledDialog";
import { HeaderIcon } from "@/modules/files/components/HeaderIcon";
import { useGetFileInfoCardLazyQuery } from "@/modules/files/graphql/files-queries.gql";
import { FILE_BROWSE_TYPES } from "@/settings/settings.ts";
import { FileEditDialogForm } from "@/ui-modules/files/FileEditDialog.tsx";
import { FileEventsCard } from "@/ui-modules/files/FileEventsCard.tsx";
import { FileResourcesCard } from "@/ui-modules/files/FileResourcesCard.tsx";

interface FileDetailsDialogProps {
  file: Pick<ProjectFile, "id" | "fileName" | "fileBrowseType" | "lastModified">;
  /** Callback passed to the Edit Dialog */
  onSaved?: () => void;
  isOpen: boolean;
  onClose?: () => void;
}

export const FileDetailsDialog: FC<FileDetailsDialogProps> = (props) => {
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState<boolean>(false);
  const { overlayProps, state } = useOverlay({});

  // Loaded within <FileCard>
  const [loadFileInfoData, { data, refetch }] = useGetFileInfoCardLazyQuery({
    variables: { fileId: props.file.id },
  });

  useEffect(() => {
    if (props.isOpen) {
      state.open();
    } else {
      state.close();
    }
  }, [props.isOpen, state]);

  useEffect(() => {
    if (state.isOpen) {
      void loadFileInfoData();
    }
  }, [loadFileInfoData, state.isOpen]);

  const [file, isRegularFile, isFolder, isHyperlink] = useMemo(() => {
    const file = data?.projectFileById;

    return [
      file,
      file?.fileBrowseType === FILE_BROWSE_TYPES.REGULAR_FILE,
      file?.fileBrowseType === FILE_BROWSE_TYPES.DIRECTORY,
      file?.fileBrowseType === FILE_BROWSE_TYPES.HYPERLINK,
    ];
  }, [data]);

  const actionWidgets = [];

  if ((isRegularFile || isFolder || isHyperlink) && file?.isEditable && props.onSaved) {
    actionWidgets.push(
      <TriggerButton
        icon={"edit"}
        onPress={() => setShowEditDialog(true)}
        tooltip={"Edit some settings of this file"}
        key={"files-page-action-edit"}
        label={"Edit"}
        showLabel
      />,
    );
  }
  if (isHyperlink && file?.url) {
    actionWidgets.push(<OpenInNewTabButton url={file.url} key={"files-page-action-open"} />);
  }
  if (file?.previewable) {
    actionWidgets.push(
      <TriggerButton
        onPress={() => {
          setShowPreviewDialog(true);
        }}
        icon={"zoom_in"}
        key={"file-details-dialog-preview-open"}
        label={"Preview"}
        showLabel
      />,
    );
  }

  return (
    <>
      <Modal state={state} {...overlayProps}>
        <TabDialogComponent
          title={props.file.fileName ?? ""}
          subtitle={
            props.file.lastModified
              ? `Modified on ${formatDateTimeFromISO(props.file.lastModified, { hideTime: true })}`
              : undefined
          }
          icon={props.file ? <HeaderIcon file={props.file} /> : undefined}
          closeFn={() => props.onClose?.()}
          width={580}
          actions={[...actionWidgets, <FileDownloadButton file={file} key={`file-details-dialog-download-button`} />]}
          disabledKeys={
            isFolder || isHyperlink ? [`file-info-dialog-resources-tab`, `file-info-dialog-events-tab`] : undefined
          }
        >
          <TabItem key={`file-info-dialog-file-tab`} title={"Info"}>
            <FileCard fileId={props.file.id} scrollable noBorder contentStyle={{ paddingTop: 20 }} />
          </TabItem>
          <TabItem key={`file-info-dialog-resources-tab`} title={"Associated Resources"}>
            <FileResourcesCard fileId={props.file.id} scrollable noBorder contentStyle={{ paddingTop: 20 }} />
          </TabItem>
          <TabItem key={`file-info-dialog-events-tab`} title={"Related Events"}>
            <FileEventsCard fileId={props.file.id} scrollable noBorder contentStyle={{ paddingTop: 20 }} />
          </TabItem>
        </TabDialogComponent>
      </Modal>

      {file && props.onSaved && (
        <FileEditDialogForm
          file={file}
          onSaved={() => {
            void refetch();
            PubSub.publish(PubSubActions.FILE_EDIT);
            setShowEditDialog(false);
            props.onSaved?.();
          }}
          onCancel={() => setShowEditDialog(false)}
          isOpen={showEditDialog}
        />
      )}

      {file && (
        <FilePreviewControlledDialog
          file={file}
          key={`file-details-dialog-preview-button`}
          onCancel={() => setShowPreviewDialog(false)}
          isOpen={showPreviewDialog}
        />
      )}
    </>
  );
};
